import {React, useState, useEffect} from 'react';
import { Card, CardBody, CardExpandToggler } from './../../components/card/card.jsx';
import Chart from 'react-apexcharts';

function Dashboard() {
    
    function randomNo() {
		return Math.floor(Math.random() * 60) + 30
	};
	
	// server chart
	var serverChartData = [
		{ name: 'MEMORY USAGE', data: [randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo()]},
		{ name: 'CPU USAGE', data: [randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo(), randomNo()]}
	];

    const [serverData, setServerData] = useState([]);
    const [activityLogData, setActivityLogData] = useState([]);
    const [chartOptions, setChartOptions] = useState(getChartOptions());
    const [serverChartOptions, setServerChartOptions] = useState(getServerChartOptions());
    const [serverHealth, setServerHealth] = useState(null);
    const [serverProcesses, setServerProcesses] = useState([]);
    const [realServerChartData, setRealServerChartData] = useState([
        { name: 'MEMORY USAGE', data: [] },
        { name: 'CPU USAGE', data: [] }
    ]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [userColors, setUserColors] = useState({});

    // API endpoints
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const HEALTH_ENDPOINT = `${API_BASE_URL}/monitor/health`;
    const JOBS_ENDPOINT = `${API_BASE_URL}/monitor/jobs`;

    // Maximum number of data points to keep in the chart
    const MAX_DATA_POINTS = 24;

    // Fetch server health data
    useEffect(() => {
        const fetchServerHealth = async () => {
            try {
                const response = await fetch(HEALTH_ENDPOINT);
                const data = await response.json();
                setServerHealth(data);
                
                // Update server chart data by appending new values
                setRealServerChartData(prevData => {
                    // Create copies of the previous data arrays
                    const memoryData = [...prevData[0].data];
                    const cpuData = [...prevData[1].data];
                    
                    // Add new data points
                    memoryData.push(data.memory.percent);
                    cpuData.push(data.cpu_percent);
                    
                    // Keep only the last MAX_DATA_POINTS data points
                    const newMemoryData = memoryData.slice(-MAX_DATA_POINTS);
                    const newCpuData = cpuData.slice(-MAX_DATA_POINTS);
                    
                    return [
                        { name: 'MEMORY USAGE', data: newMemoryData },
                        { name: 'CPU USAGE', data: newCpuData }
                    ];
                });

                // Update server data for display
                setServerData([
                    {
                        name: 'SERVER HEALTH',
                        total: `${data.cpu_percent.toFixed(1)}% CPU`,
                        progress: `${data.cpu_percent}%`,
                        time: `Updated: ${new Date(data.timestamp).toLocaleTimeString()}`,
                        chartType: 'line',
                        chartHeight: '50px',
                        chartData: [{ data: [data.cpu_percent, data.memory.percent, data.disk.percent] }],
                        info: [
                            { title: 'Memory', value: `${data.memory.percent}%`, class: 'text-theme' },
                            { title: 'Disk', value: `${data.disk.percent}%`, class: 'text-blue' },
                            { title: 'Load Avg (1m)', value: data.load_avg['1m'].toFixed(2), class: 'text-warning' }
                        ]
                    },
                    {
                        name: 'SYSTEM RESOURCES',
                        total: `${(data.memory.used / (1024 * 1024 * 1024)).toFixed(2)} GB RAM Used`,
                        progress: `${data.memory.percent}%`,
                        time: `Total: ${(data.memory.total / (1024 * 1024 * 1024)).toFixed(2)} GB`,
                        chartType: 'pie',
                        chartHeight: '50px',
                        chartData: [data.memory.percent, data.disk.percent, 100 - data.memory.percent],
                        info: [
                            { title: 'Disk Used', value: `${(data.disk.used / (1024 * 1024 * 1024)).toFixed(2)} GB`, class: 'text-blue' },
                            { title: 'Uptime', value: `${(data.uptime / 86400).toFixed(1)} days`, class: 'text-success' }
                        ]
                    }
                ]);
            } catch (error) {
                console.error('Error fetching server health:', error);
            }
        };

        fetchServerHealth();
        const interval = setInterval(fetchServerHealth, 10000); // Update every 10 seconds
        
        return () => clearInterval(interval);
    }, []);

    // Fetch server processes data
    useEffect(() => {
        const fetchServerProcesses = async () => {
            try {
                const response = await fetch(JOBS_ENDPOINT);
                const data = await response.json();
                setServerProcesses(data.processes || []);
                
                // Format process data for activity log and sort by CPU and memory usage
                const processLogs = (data.processes || [])
                    .map(process => ({
                        title: `${process.name} (PID: ${process.pid})`,
                        user: `${process.user}`,
                        highlight: process.cpu_percent > 1.0 || process.mem_percent > 5.0,
                        details: `CPU: ${process.cpu_percent.toFixed(2)}%, MEM: ${process.mem_percent.toFixed(2)}%`,
                        cmd: process.cmdline,
                        resourceScore: (process.cpu_percent * 10) + (process.mem_percent * 100),
                        cpuPercent: process.cpu_percent,
                        memPercent: process.mem_percent
                    }))
                    // Sort by resource usage (CPU and memory combined)
                    .sort((a, b) => b.resourceScore - a.resourceScore)
                    // Take top 20 processes
                    .slice(0, 20);
                
                // Generate colors for unique users
                const uniqueUsers = [...new Set(processLogs.map(log => log.user))];
                const colors = ['#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8', '#6f42c1', '#fd7e14', '#20c997', '#e83e8c', '#6c757d'];
                const newUserColors = {};
                uniqueUsers.forEach((user, index) => {
                    newUserColors[user] = colors[index % colors.length];
                });
                setUserColors(newUserColors);
                
                setActivityLogData(processLogs);
            } catch (error) {
                console.error('Error fetching server processes:', error);
            }
        };

        fetchServerProcesses();
        const interval = setInterval(fetchServerProcesses, 10000); // Update every 10sec
        
        return () => clearInterval(interval);
    }, []);

    // Function to handle sorting
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Function to sort data
    const getSortedData = () => {
        if (!sortConfig.key) return activityLogData;
        
        return [...activityLogData].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'title':
                    aValue = a.title.toLowerCase();
                    bValue = b.title.toLowerCase();
                    break;
                case 'user':
                    aValue = a.user.toLowerCase();
                    bValue = b.user.toLowerCase();
                    break;
                case 'cpu':
                    aValue = a.cpuPercent;
                    bValue = b.cpuPercent;
                    break;
                case 'memory':
                    aValue = a.memPercent;
                    bValue = b.memPercent;
                    break;
                case 'cmd':
                    aValue = a.cmd.toLowerCase();
                    bValue = b.cmd.toLowerCase();
                    break;
                default:
                    return 0;
            }
            
            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    // Get sorted data
    const sortedActivityData = getSortedData();

    function getChartOptions() {
		var themeColor = (getComputedStyle(document.body).getPropertyValue('--bs-theme')).trim();
		var themeColorRgb = (getComputedStyle(document.body).getPropertyValue('--bs-theme-rgb')).trim();
		
		return {
			bar: {
				colors: [themeColor],
				chart: { sparkline: { enabled: true } },
				tooltip: { x: { show: false }, y: { title: { formatter: function (seriesName) { return '' } } } }
			},
			line: {
				colors: [themeColor],
				chart: { sparkline: { enabled: true } },
				stroke: { curve: 'straight', width: 2 },
				tooltip: { x: { show: false }, y: { title: { formatter: function (seriesName) { return '' } } } }
			},
			pie: {
				colors: ['rgba('+ themeColorRgb + ', 1)', 'rgba('+ themeColorRgb + ', .75)', 'rgba('+ themeColorRgb + ', .5)'],
				chart: { sparkline: { enabled: true } },
				stroke: { show: false },
				tooltip: { x: { show: false }, y: { title: { formatter: function (seriesName) { return '' } } } }
			},
			donut: {
				colors: ['rgba('+ themeColorRgb + ', .15)', 'rgba('+ themeColorRgb + ', .35)', 'rgba('+ themeColorRgb + ', .55)', 'rgba('+ themeColorRgb + ', .75)', 'rgba('+ themeColorRgb + ', .95)'],
				chart: { sparkline: { enabled: true } },
				stroke: { show: false },
				tooltip: { x: { show: false }, y: { title: { formatter: function (seriesName) { return '' } } } },
			}
		};
  }

    function getServerChartOptions() {
        var borderColor = (getComputedStyle(document.body).getPropertyValue('--bs-border-color')).trim();
        var bodyColor = (getComputedStyle(document.body).getPropertyValue('--bs-body-color')).trim();
        var inverseRgb = (getComputedStyle(document.body).getPropertyValue('--bs-inverse-rgb')).trim();
        var themeColor = (getComputedStyle(document.body).getPropertyValue('--bs-theme')).trim();
        var themeFont = (getComputedStyle(document.body).getPropertyValue('--bs-body-font-family')).trim();
          
        return {
          chart: { 
              toolbar: { show: false },
              animations: { enabled: true },
              type: 'line'
          },
          plotOptions: { bar: { horizontal: false, columnWidth: '55%', endingShape: 'rounded' } },
          dataLabels: { enabled: false },
          grid: { show: true, borderColor: borderColor },
          stroke: { 
              show: true, 
              curve: 'smooth',
              width: 2
          },
          colors: ['rgba('+ inverseRgb +', .75)', themeColor],
          legend: { fontFamily: themeFont, labels: { colors: bodyColor } },
          xaxis: {
              type: 'numeric',
              labels: { 
                  show: true,
                  formatter: function(value) {
                      // Show only some labels to avoid crowding
                      return value % 4 === 0 ? value : '';
                  },
                  style: {
                      colors: bodyColor,
                      fontSize: '10px',
                      fontFamily: themeFont
                  }
              },
              axisBorder: { show: true, color: borderColor, height: 1, width: '100%', offsetX: 0, offsetY: -1 },
              axisTicks: { show: true, borderType: 'solid', color: borderColor, height: 6, offsetX: 0, offsetY: 0 },
          },
          yaxis: { 
              labels: { 
                  style: { colors: bodyColor, fontSize: '12px', fontFamily: themeFont, fontWeight: 400 },
                  formatter: function(value) {
                      return value + '%';
                  }
              },
              min: 0,
              max: 100,
              tickAmount: 5
          },
          fill: { opacity: 0.65, type: 'solid' },
          tooltip: { 
              y: { formatter: function (val) { return val + "%" } },
              x: { show: false }
          }
        };
    }

	return (
		<div>
			<ul className="breadcrumb">
				<li className="breadcrumb-item"><a href="#/">Admin</a></li>
				<li className="breadcrumb-item active">DASHBOARD</li>
			</ul>
		
			<h1 className="page-header">
				DASHBOARD <small>statistics from CPAI server</small>
			</h1>
		
        <div className="row">
            <div className="col-xl-6">
					<Card className="mb-3">
						<CardBody>
							<div className="d-flex fw-bold small mb-3">
								<span className="flex-grow-1">SERVER STATS</span>
								<CardExpandToggler />
							</div>
							<div className="ratio ratio-21x9 mb-3">
								<Chart type="line" height="100%" options={ serverChartOptions} series={realServerChartData} />
							</div>
							<div className="row">
								{ serverData && serverData.length > 0 ? (  serverData.map((server, index) => (
									<div className="col-lg-6 mb-3 mb-lg-0" key={index}>
										<div className="d-flex">
											<div className="w-50px pt-3">
												<Chart type={server.chartType} height={server.chartHeight} options={  chartOptions[server.chartType]} series={server.chartData} />
											</div>
											<div className="ps-3 flex-1">
												<div className="fs-10px fw-bold text-inverse text-opacity-50 mb-1">{server.name}</div>
												<div className="mb-2 fs-5 text-truncate">{server.total}</div>
												<div className="progress h-3px bg-inverse-transparent-2 mb-1">
													<div className="progress-bar bg-theme" style={{width: server.progress}}></div>
												</div>
												<div className="fs-11px text-inverse text-opacity-50 mb-2 text-truncate">
													{server.time}
												</div>
												{server.info.length > 0 ? (server.info.map((info, index) => (
													<div className="d-flex align-items-center small" key={index}>
														<i className={'bi bi-circle-fill fs-6px me-2 '+ info.class}></i> 
														<div className="flex-1">{info.title}</div>
														<div>{info.value}</div> 
													</div>
												))) : (
													<div>No records found</div>
												)}
											</div>
										</div>
									</div>
								))) : (
									<div className="col-lg-12">Loading server data...</div>
								)}
							</div>
						</CardBody>
					</Card>
				</div>

                <div className="col-xl-6">
					<Card className="mb-3">
						<CardBody>
							<div className="d-flex fw-bold small mb-3">
								<span className="flex-grow-1">ACTIVITY JOBS</span>
								<CardExpandToggler />
							</div>
							<div className="table-responsive" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
								<table className="table table-striped table-borderless mb-2px small text-nowrap">
									<thead className="sticky-top">
										<tr>
											<th 
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => handleSort('title')}
											>
												Process Name ({activityLogData.length})
												{sortConfig.key === 'title' && (
													<i className={`bi bi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
												)}
											</th>                                            
											<th 
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => handleSort('cpu')}
											>
												CPU Usage
												{sortConfig.key === 'cpu' && (
													<i className={`bi bi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
												)}
											</th>
											<th 
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => handleSort('memory')}
											>
												Memory Usage
												{sortConfig.key === 'memory' && (
													<i className={`bi bi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
												)}
											</th>
											<th 
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => handleSort('user')}
											>
												User
												{sortConfig.key === 'user' && (
													<i className={`bi bi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
												)}
											</th>
											<th 
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => handleSort('cmd')}
											>
												CMD
												{sortConfig.key === 'cmd' && (
													<i className={`bi bi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
												)}
											</th>
										</tr>
									</thead>
									<tbody>
										{sortedActivityData && sortedActivityData.length > 0 ? (sortedActivityData.map((log, index) => (
											<tr key={index}>
												<td>
													<span className="d-flex align-items-center">
														<i className={'bi bi-circle-fill fs-6px me-2 ' + ((log.highlight) ? 'text-theme' : 'text-inverse text-opacity-30')}></i> 
														{log.title}                                                        
													</span>
												</td>                                                
												<td><small className="fw-bold">{log.cpuPercent.toFixed(2)}%</small></td>
												<td><small className="fw-bold">{log.memPercent.toFixed(2)}%</small></td>
												<td>
													<span 
														className="badge d-block rounded-0 pt-5px w-70px text-white fw-bold" 
														style={{ 
															minHeight: '18px',
															backgroundColor: userColors[log.user] || '#6c757d'
														}}
													>
														{log.user}
													</span>
												</td>
                                                <td><small>{log.cmd}</small></td>
												{/* <td><a href="#/" className="text-decoration-none text-inverse"><i className="bi bi-search"></i></a></td> */}
											</tr>
										))) : (
											<tr>
												<td colSpan="5">Loading process data...</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</CardBody>
					</Card>
				</div>
        </div>
        </div>
	)
}

export default Dashboard;